import React, { useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Camera, Loader2, X, Check, RefreshCw, Image as ImageIcon, Sparkles, AlertCircle } from "lucide-react";
import { isNativeApp } from "../lib/platform";
import { haptic } from "../lib/haptics";
import { fileToResizedBase64, dataUrlToResizedBase64 } from "../lib/imageResize";

const MEALS = ["breakfast", "lunch", "dinner", "snack"];

export default function PhotoMealCard({ onLogged }) {
  const fileRef = useRef(null);
  const [meal, setMeal] = useState("snack");
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [estimate, setEstimate] = useState(null); // { name, calories, protein_g, carbs_g, fat_g, confidence, assumptions, items }
  const [confirming, setConfirming] = useState(false);

  const reset = () => {
    setPreviewUrl(null);
    setEstimate(null);
    setHint("");
    if (fileRef.current) fileRef.current.value = "";
  };

  // Shared post-acquisition pipeline: resize → POST → store estimate.
  const analyzeFromBase64 = async (base64, previewUrl) => {
    setBusy(true);
    setEstimate(null);
    setPreviewUrl(previewUrl);
    try {
      const { data } = await api.post("/food/photo-estimate", {
        image_base64: base64,
        mime_type: "image/jpeg",
        meal,
        hint: hint || undefined,
      });
      setEstimate(data);
      haptic.tap();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not analyze the photo");
      setPreviewUrl(null);
    } finally {
      setBusy(false);
    }
  };

  // Web / non-native: <input type=file> → File → base64
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      toast.error("Use JPG, PNG or WEBP");
      e.target.value = "";
      return;
    }
    try {
      const { base64, previewUrl } = await fileToResizedBase64(file);
      await analyzeFromBase64(base64, previewUrl);
    } catch (err) {
      toast.error("Could not read the photo");
    }
  };

  // Native (iOS / Android): Capacitor Camera → action sheet ("Take Photo" / "Choose")
  const onNativeCapture = async () => {
    try {
      // Dynamic import keeps `@capacitor/camera` out of the web bundle's
      // critical path; webpack code-splits it into its own chunk.
      const { Camera: CapCamera, CameraResultType, CameraSource } = await import("@capacitor/camera");
      const photo = await CapCamera.getPhoto({
        quality: 85,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Prompt,   // shows iOS action sheet
        promptLabelHeader: "Add a meal photo",
        promptLabelPhoto: "Choose from library",
        promptLabelPicture: "Take photo",
        correctOrientation: true,
        width: 1280,
      });
      if (!photo?.dataUrl) return;
      const { base64, previewUrl } = await dataUrlToResizedBase64(photo.dataUrl);
      await analyzeFromBase64(base64, previewUrl);
    } catch (err) {
      // User cancelling the sheet throws "User cancelled photos app" — silently ignore.
      const msg = err?.message || "";
      if (/cancel/i.test(msg)) return;
      console.error("native camera capture failed:", err);
      toast.error("Could not open the camera");
    }
  };

  const triggerUpload = () => {
    if (isNativeApp()) {
      onNativeCapture();
    } else {
      fileRef.current?.click();
    }
  };

  const updateField = (k, v) =>
    setEstimate((e) => ({ ...e, [k]: k === "name" ? v : Number(v) || 0 }));

  const confirmLog = async () => {
    if (!estimate) return;
    setConfirming(true);
    try {
      await api.post("/food/log", {
        name: estimate.name || "Photo meal",
        calories: Number(estimate.calories) || 0,
        protein_g: Number(estimate.protein_g) || 0,
        carbs_g: Number(estimate.carbs_g) || 0,
        fat_g: Number(estimate.fat_g) || 0,
        meal,
      });
      haptic.success();
      toast.success("Meal logged from photo");
      reset();
      onLogged?.();
    } catch (err) {
      haptic.error();
      toast.error("Failed to log meal");
    } finally {
      setConfirming(false);
    }
  };

  const confidenceColor = {
    high: "text-[#6B9BFF] border-[#6B9BFF]/40 bg-[#6B9BFF]/5",
    medium: "text-[#FFB020] border-[#FFB020]/40 bg-[#FFB020]/5",
    low: "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/5",
  };

  return (
    <div className="tracing-border p-6" data-testid="photo-meal-card">
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow text-[#6B9BFF] flex items-center gap-2">
          <Camera className="w-3.5 h-3.5" /> Snap a meal
        </div>
        {estimate && (
          <button
            onClick={reset}
            className="text-xs text-white/40 hover:text-white inline-flex items-center gap-1"
            data-testid="photo-reset"
          >
            <RefreshCw className="w-3 h-3" /> Try another
          </button>
        )}
      </div>

      {/* Upload area */}
      {!previewUrl && (
        <div className="space-y-3">
          <p className="text-white/60 text-sm">
            Take or upload a photo of your meal and Claude will estimate the calories & macros. Edit before you log.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={meal}
              onChange={(e) => setMeal(e.target.value)}
              className="bg-[#1A1A1A] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF] sm:w-32"
              data-testid="photo-meal-select"
            >
              {MEALS.map((mm) => (
                <option key={mm} value={mm}>{mm}</option>
              ))}
            </select>
            <input
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              placeholder='Hint e.g. "no oil"'
              className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF] text-sm"
              data-testid="photo-hint"
            />
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            className="hidden"
            onChange={onPick}
            data-testid="photo-file-input"
          />
          <button
            type="button"
            onClick={triggerUpload}
            disabled={busy}
            className="w-full border-2 border-dashed border-white/15 hover:border-[#6B9BFF] hover:bg-[#6B9BFF]/5 transition-colors py-10 flex flex-col items-center gap-3 disabled:opacity-50"
            data-testid="photo-upload-trigger"
          >
            {busy ? (
              <>
                <Loader2 className="w-8 h-8 text-[#6B9BFF] animate-spin" />
                <span className="text-sm text-white/70">Analyzing your meal…</span>
              </>
            ) : (
              <>
                <Camera className="w-8 h-8 text-white/60" />
                <div className="text-center">
                  <div className="font-bold text-white">Take / upload photo</div>
                  <div className="text-xs text-white/40 mt-1">JPG / PNG / WEBP · auto-resized to 1280px</div>
                </div>
              </>
            )}
          </button>
        </div>
      )}

      {/* Preview + edit */}
      {previewUrl && (
        <div className="space-y-4">
          <div className="relative">
            <img
              src={previewUrl}
              alt="meal preview"
              className="w-full max-h-72 object-cover border border-white/10"
              data-testid="photo-preview"
            />
            <button
              onClick={reset}
              className="absolute top-2 right-2 w-8 h-8 bg-black/70 backdrop-blur flex items-center justify-center text-white hover:bg-[#FF3B30]"
              data-testid="photo-clear"
              aria-label="Remove photo"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {busy && (
            <div className="flex items-center gap-2 text-white/70 text-sm py-4">
              <Loader2 className="w-4 h-4 animate-spin text-[#6B9BFF]" />
              Analyzing…
            </div>
          )}

          {estimate && (
            <div className="space-y-4" data-testid="photo-estimate">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="label-eyebrow text-white/50 flex items-center gap-2">
                  <Sparkles className="w-3 h-3 text-[#6B9BFF]" /> AI estimate (edit if needed)
                </div>
                <span
                  className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 border ${confidenceColor[estimate.confidence] || confidenceColor.medium}`}
                  data-testid="photo-confidence"
                >
                  {estimate.confidence} confidence
                </span>
              </div>

              {estimate.items?.length > 0 && (
                <div className="text-xs text-white/50">
                  <span className="text-white/40">Detected: </span>
                  {estimate.items.join(", ")}
                </div>
              )}
              {estimate.assumptions && (
                <div className="text-xs text-white/40 italic flex items-start gap-1.5">
                  <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                  {estimate.assumptions}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="label-eyebrow text-white/50 block mb-1.5 text-[10px]">Name</label>
                  <input
                    value={estimate.name}
                    onChange={(e) => updateField("name", e.target.value)}
                    className="w-full bg-[#1A1A1A] border border-white/10 px-3 py-2.5 focus:outline-none focus:border-[#6B9BFF] text-sm"
                    data-testid="photo-edit-name"
                  />
                </div>
                <div>
                  <label className="label-eyebrow text-white/50 block mb-1.5 text-[10px]">Calories</label>
                  <input
                    type="number"
                    value={estimate.calories}
                    onChange={(e) => updateField("calories", e.target.value)}
                    className="w-full bg-[#1A1A1A] border border-white/10 px-3 py-2.5 focus:outline-none focus:border-[#6B9BFF] text-sm"
                    data-testid="photo-edit-calories"
                  />
                </div>
                <div>
                  <label className="label-eyebrow text-white/50 block mb-1.5 text-[10px]">Meal</label>
                  <select
                    value={meal}
                    onChange={(e) => setMeal(e.target.value)}
                    className="w-full bg-[#1A1A1A] border border-white/10 px-3 py-2.5 focus:outline-none focus:border-[#6B9BFF] text-sm"
                    data-testid="photo-edit-meal"
                  >
                    {MEALS.map((mm) => (
                      <option key={mm} value={mm}>{mm}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label-eyebrow text-white/50 block mb-1.5 text-[10px]">Protein g</label>
                  <input
                    type="number"
                    value={estimate.protein_g}
                    onChange={(e) => updateField("protein_g", e.target.value)}
                    className="w-full bg-[#1A1A1A] border border-white/10 px-3 py-2.5 focus:outline-none focus:border-[#6B9BFF] text-sm"
                    data-testid="photo-edit-protein"
                  />
                </div>
                <div>
                  <label className="label-eyebrow text-white/50 block mb-1.5 text-[10px]">Carbs g</label>
                  <input
                    type="number"
                    value={estimate.carbs_g}
                    onChange={(e) => updateField("carbs_g", e.target.value)}
                    className="w-full bg-[#1A1A1A] border border-white/10 px-3 py-2.5 focus:outline-none focus:border-[#6B9BFF] text-sm"
                    data-testid="photo-edit-carbs"
                  />
                </div>
                <div>
                  <label className="label-eyebrow text-white/50 block mb-1.5 text-[10px]">Fat g</label>
                  <input
                    type="number"
                    value={estimate.fat_g}
                    onChange={(e) => updateField("fat_g", e.target.value)}
                    className="w-full bg-[#1A1A1A] border border-white/10 px-3 py-2.5 focus:outline-none focus:border-[#6B9BFF] text-sm"
                    data-testid="photo-edit-fat"
                  />
                </div>
              </div>

              <button
                disabled={confirming}
                onClick={confirmLog}
                className="w-full bg-[#6B9BFF] text-black font-black uppercase tracking-widest py-3 hover:bg-white transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="photo-confirm-log"
              >
                {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Confirm & log
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
